/**
 * This module exports a draggable Component which is drawn in Workspace
 * @module components/assembly/Component
 */

import React, { Component } from 'react'
import { connect } from 'react-redux'
import { DragSource } from 'react-dnd-old'
import ItemTypes from './ItemTypes'
import DraggingInfo from './DraggingInfo'
import { getEmptyImage } from 'react-dnd-html5-backend-old'
import renderCompImg from '../../source/Comp_Img'
import renderImage from '../../source/importImg'
import removeSensorProgramData from '../../utils/removeSensorProgramData'
import removeHumanoidLocalProgramData from '../../utils/removeHumanoidLocalProgramData'
import ReactDOM, { createPortal } from 'react-dom'
import {
    HardwareWireConfigs,
    getHardwareWireConfig,
    getJoystickWireConfig,
    isJoystickWireType,
} from './PinMaps'
import renderPrgImage from '../../source/programImg'
import './Assembly.css'
import './Component.css'
import { Settings } from 'lucide-react'
import { getDeviceImagePosition } from './deviceImageOffsets'
import { getConnectedDevicePinout } from '../../hooks/useBrandConfig'

const BOARD_IMAGE_HEIGHT = 300

var clickStartTimestamp = undefined // Posible as only one component can be clicked at a particular time

const style = {
    position: 'absolute',
    cursor: 'move',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    zIndex: 2,
}

const isPcVersion1 =
    sessionStorage.getItem('deviceVersion')?.[0] == '1' &&
    sessionStorage.getItem('connectedDevice') === 'Ace'
const isPcVersion01 =
    sessionStorage.getItem('deviceVersion')?.[0] == '0' &&
    sessionStorage.getItem('connectedDevice') === 'Ace'
let isDeviceVersion12 =
    (['1', '2', '3'].includes(
        (sessionStorage.getItem('deviceVersion') || '0.0.0')[0]
    ) &&
        ['Ace', 'Humanoid', 'Hexapod'].includes(
            sessionStorage.getItem('connectedDevice')
        )) ||
    ['Roboki', 'Klaw', 'Atr', 'Toki', 'Poki', 'Moki', 'woofKit'].includes(
        sessionStorage.getItem('connectedDevice')
    )

// Hardcoded board-port anchors, expressed as a fraction (0–100) of the board's
// on-screen rect (so they track the board as it pans/zooms). Each port has three
// independent points:
//   x / y           — where the magnifier lens SITS on screen
//   hoverX / hoverY — the hover hot-spot that reveals the lens
//   focusX / focusY — which area the lens MAGNIFIES (shown at its centre)
// focusX/focusY default to x/y when omitted, so set them only when you want the
// lens to zoom a different area than where it sits.
const BOARD_PORT_POSITIONS = {
    S1: { x: 0, y: 10, hoverX: 30, hoverY: 40 },
    S2: { x: 100, y: 10, hoverX: 70, hoverY: 40 },
    A1: { x: 0, y: 70, hoverX: 30, hoverY: 60 },
    A2: { x: 100, y: 70, hoverX: 70, hoverY: 60 },
    M1: { x: 10, y: 110, hoverX: 32, hoverY: 85 },
    M2: { x: 90, y: 110, hoverX: 68, hoverY: 85 },
}
const POKI_PORT_POSITIONS = {
    S1: { x: 10, y: 20, hoverX: 25, hoverY: 30 },
}
// How close (in px) the cursor must be to a port anchor to reveal its magnifier.
const BOARD_PORT_HOVER_RADIUS = 25

// Component types for which the component pin magnifier POC must never render.
const MAGNIFIER_EXCLUDED_COMPONENTS = [
    'servo_motor',
    'servo_motor_360',
    'servo_motor_270',
    'dc_motor',
    'dynamex_motor',
    'mini_geared_motor',
    'geared_motor',
]

// Live-value overlay registry: the single source of truth for which streaming
// sensors paint a value <p> on top of their graphic, and the fine per-type
// nudge (marginTop/marginLeft) that aligns the text over the reading area.
//
// Every magnifier-eligible type renders through the magnifier branch on
// Toki/Poki, so the overlay used to vanish there. Both the magnifier branch and
// each sensor's dedicated branch now read from this table via
// renderLiveValueOverlay(), so the value tracks the component identically in
// either render path and on every port (A/B/C/D *and* servo_motor1/2). Adding a
// new streaming sensor is a one-line entry here — no copied JSX, no port switch.
const LIVE_VALUE_OVERLAY_STYLES = {
    ultrasonic_sensor: { marginTop: '-0.7%', marginLeft: '3%' },
    Analog_3_Pin_Input: { marginTop: '-15px', marginLeft: '15px' },
    Analog_2_Pin_Input: { marginTop: '-15px', marginLeft: '15px' },
    Digital_3_Pin_Input: { marginTop: '-15px', marginLeft: '15px' },
    Digital_2_Pin_Input: { marginTop: '-15px', marginLeft: '15px' },
    tern_touch_sensor: { marginTop: '-15px', marginLeft: '15px' },
    tern_humidity_sensor: { marginTop: '-15px', marginLeft: '15px' },
    tern_gas_sensor: { marginTop: '-15px', marginLeft: '15px' },
    tern_sound_sensor: { marginTop: '-15px', marginLeft: '15px' },
    tern_tact_switch: { marginTop: '-15px', marginLeft: '15px' },
}

// Map a raw connected-port string (e.g. "S2", "servo_motor1", "A1") to the
// board-port key whose magnifier lens represents it (S1/S2/A1/A2). Returns null
// when the string doesn't correspond to any board port.
const mapPortToBoardKey = (portString) => {
    if (!portString) return null
    if (portString.includes('S1') || portString.includes('motor1')) return 'S1'
    if (portString.includes('S2') || portString.includes('motor2')) return 'S2'
    // Motor ports: M1 has its own lens; the M3 port is shown to users as "M2", so
    // it maps to the M2 board anchor (see BOARD_PORT_POSITIONS).
    if (portString.includes('M1')) return 'M1'
    if (portString.includes('M3')) return 'M2'
    if (portString.includes('A2')) return 'A2'
    if (portString.includes('A1') || portString.includes('A')) return 'A1'
    return null
}

const componentSource = {
    // Fixed kit components (woofKit servos on S1/S2/A1/A2) are not draggable.
    canDrag(props) {
        return !props.fixed
    },
    beginDrag(props) {
        DraggingInfo.isDragging = true
        const { type, index, left, top, connectedTo, wireConnections } = props
        // console.log("component@@", props);
        sessionStorage.setItem('dragingItem', props.type)
        props.removeConnection({ type, index })
        if (type !== 'joystick' && type !== 'joystick_v2') {
            try {
                document.getElementById(type + props.index).style.opacity = 0
            } catch (E) {}
        }
        DraggingInfo.newComponentPort = connectedTo
        DraggingInfo.draggingComponentOld = { type, index }
        // Poki: if this component needs + / - but no free power pin exists, the
        // drop will be rejected — warn now, at drag-start (no highlight shows).
        if (window.appState && window.appState.warnIfPowerBlocked)
            window.appState.warnIfPowerBlocked(type, { type, index })
        // Carry wireConnections so the drag preview only shows the wires that are
        // actually connected (joystick_v2 per-wire routing).
        return { type, index, left, top, connectedTo, wireConnections }
    },
    endDrag() {
        clickStartTimestamp = undefined
        DraggingInfo.isDragging = false
        DraggingInfo.draggingComponentOld = null
        DraggingInfo.newComponentPort = null
    },
}
function collect(connect, monitor) {
    return {
        connectDragSource: connect.dragSource(),
        connectDragPreview: connect.dragPreview(),
        isDragging: monitor.isDragging(),
    }
}
// var Component = React.createClass({
class Component1 extends Component {
    constructor(props) {
        super(props)
        this.rgbContainerRef = React.createRef()
        this.handleClickTimeout = null
        // this.removeComponent = this.removeComponent.bind(this);
        // () => this.typeCheck(this) = () => this.typeCheck(this)(this)
    }

    componentDidMount() {
        document.addEventListener('click', this.handleDocumentClick)
        document.addEventListener('mousemove', this.handleBoardPortHover)
    }

    componentDidUpdate() {
        this.props.connectDragPreview(getEmptyImage(), {
            // IE fallback: specify that we'd rather screenshot the node
            // when it already knows it's being dragged so we can hide it with CSS.
            //   captureDraggingState: true,
        })
        document.addEventListener('mouseleave', this.mouseOutOfBound)
    }
    componentWillUnmount = () => {
        document.removeEventListener('mouseleave', this.mouseOutOfBound)
        document.removeEventListener('click', this.handleDocumentClick)
        document.removeEventListener('mousemove', this.handleBoardPortHover)
    }

    // Reveal a board port's magnifier only while the cursor is near that port.
    // Replaces the old "open all magnifiers when the component is clicked" behaviour.
    // The component's own pin magnifier is unaffected (it still toggles on click).
    handleBoardPortHover = (e) => {
        // Only magnifier-enabled components on board devices participate.
        if (!HardwareWireConfigs[this.props.type]) return
        if (
            !['Toki', 'Poki', 'Moki', 'woofKit'].includes(
                sessionStorage.getItem('connectedDevice')
            )
        )
            return

        // HOTFIX: If the magnifier is explicitly open, do not clear or alter
        // the viewports via hover movements.
        if (this.state && this.state.rgbMagnifierOpen) return

        const boardEl = document.getElementById('PC_dragSource')
        if (!boardEl) return
        const r = boardEl.getBoundingClientRect()

        // Poki exposes a different set of board-port magnifier anchors than the
        // Toki/Moki board (see POKI_PORT_POSITIONS). Pick the right map for the
        // connected device so the hover targets match the rendered board.
        const activePortMap =
            (sessionStorage.getItem('connectedDevice') === 'Poki' || sessionStorage.getItem('connectedDevice') === 'woofKit')
                ? POKI_PORT_POSITIONS
                : BOARD_PORT_POSITIONS

        // Pick the closest port within the hover radius (null if none is near).
        let nearest = null
        let nearestDist = BOARD_PORT_HOVER_RADIUS
        Object.entries(activePortMap).forEach(([port, pos]) => {
            const px = r.left + (pos.hoverX / 100) * r.width
            const py = r.top + (pos.hoverY / 100) * r.height
            const dist = Math.hypot(e.clientX - px, e.clientY - py)
            if (dist < nearestDist) {
                nearestDist = dist
                nearest = port
            }
        })

        const current = this.state ? this.state.hoveredBoardPort : null
        if (nearest !== current) {
            this.setState({ hoveredBoardPort: nearest })
        }
    }

    mouseOutOfBound = (event) => {
        if (event.clientX < 0 && this.props.isDragging) {
            this.removeComponent()
        }
    }

    handleDocumentClick = (e) => {
        if (
            this.rgbContainerRef.current &&
            !this.rgbContainerRef.current.contains(e.target)
        ) {
            // Force reset everything back to safe defaults on true outside canvas click
            this.setState({
                rgbMagnifierOpen: false,
                hoveredBoardPort: null,
            })
        }
    }

    checkForLongPress = () => {
        if (!clickStartTimestamp) return
        else if (Date.now() - clickStartTimestamp > 1000) {
            this.removeComponent()
        } else if (this.hasJoystickWiring()) {
            // Short click on a wire-enabled joystick_v2 toggles its magnifier loupe.
            if (window.assemblyAppState) {
                window.assemblyAppState.toggleMagnifier(
                    this.props.type,
                    this.props.index
                )
            } else {
                // TEMP DIAGNOSTIC - remove after fix.
                console.log('[JSDBG] click: no window.assemblyAppState')
            }
        } else if (isJoystickWireType(this.props.type)) {
            // TEMP DIAGNOSTIC - remove after fix.
            console.log('[JSDBG] click: hasJoystickWiring() === false', {
                type: this.props.type,
                connectedDevice: sessionStorage.getItem('connectedDevice'),
                deviceVersion: sessionStorage.getItem('deviceVersion'),
                connectedTo: this.props.connectedTo,
            })
        }
        clickStartTimestamp = undefined

        //isSave = false when delete connections with double clicks
        sessionStorage.setItem('isSave', JSON.stringify(false))
    }

    hasJoystickWiring = () =>
        isJoystickWireType(this.props.type) &&
        !!getJoystickWireConfig(
            this.props.type,
            sessionStorage.getItem('connectedDevice'),
            this.props.connectedTo
        )

    // Render the circular magnifier loupe + the two wire buttons (A / B) when this
    // component's magnifier is open. Positioned relative to the component's own div
    // (its parent), centred just above it, so it shares the component's coordinate
    // space (no viewport-portal offset math needed). Clicking a button starts
    // routing that wire.
    renderJoystickMagnifier = (anchorLeft, anchorTop, compW) => {
        const cfg = getJoystickWireConfig(
            this.props.type,
            sessionStorage.getItem('connectedDevice'),
            this.props.connectedTo
        )
        // TEMP DIAGNOSTIC - remove after fix.
        if (isJoystickWireType(this.props.type))
            console.log('[JSDBG] renderJoystickMagnifier', {
                hasCfg: !!cfg,
                magnifierProp: this.props.magnifier,
                type: this.props.type,
                index: this.props.index,
                connectedTo: this.props.connectedTo,
                hasCompEl: !!document.getElementById(
                    this.props.type + this.props.index
                ),
            })
        if (!cfg) return null
        const m = this.props.magnifier
        if (!m || m.type !== this.props.type || m.index !== this.props.index)
            return null

        const { scale } = this.props
        const size = cfg.magnifier.size

        // 1. Grab the current component wrapper element to calculate standard viewport offsets
        const compEl = document.getElementById(
            this.props.type + this.props.index
        )
        if (!compEl) return null
        const rect = compEl.getBoundingClientRect()

        // 2. Convert standard canvas layouts to global fixed-viewport variables
        const portalLeft = rect.left + rect.width / 2 - size / 2 + 70
        const portalTop = rect.top - size + 230 * scale
        const cx = size / 2
        const cy = size / 2

        // 3. Mount into document.body using createPortal
        return createPortal(
            <div
                style={{
                    position: 'fixed', // Changed from absolute to fixed
                    left: portalLeft,
                    top: portalTop,
                    width: size,
                    height: size,
                    zIndex: 9999, // Safely sits on top of all application layers
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: size,
                        height: size,
                        borderRadius: '50%',
                        border: '3px solid #b0b0b0',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        backgroundColor: '#fff',
                        backgroundImage: `url(${renderCompImg(this.props.type)})`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: `${cfg.magnifier.bgX} ${cfg.magnifier.bgY}`,
                        backgroundSize: `${cfg.magnifier.zoom * 100}%`,
                        overflow: 'hidden',
                    }}
                />
                <svg
                    width={size}
                    height={size}
                    style={{ position: 'absolute', left: 0, top: 0 }}
                >
                    {Object.keys(cfg.wires).map((wireKey) => {
                        const w = cfg.wires[wireKey]
                        return (
                            <line
                                key={wireKey}
                                x1={cx + w.offsetStart[0]}
                                y1={cy + w.offsetStart[1]}
                                x2={cx + w.offsetEnd[0]}
                                y2={cy + w.offsetEnd[1]}
                                stroke={w.stroke}
                                strokeWidth="3"
                            />
                        )
                    })}
                </svg>
                {Object.keys(cfg.wires).map((wireKey) => {
                    const w = cfg.wires[wireKey]
                    const btn = 22
                    return (
                        <button
                            key={wireKey}
                            onMouseDown={(e) => {
                                e.stopPropagation()
                                if (window.assemblyAppState) {
                                    window.assemblyAppState.startWire(
                                        this.props.type,
                                        this.props.index,
                                        wireKey
                                    )
                                }
                            }}
                            style={{
                                position: 'absolute',
                                left: cx + w.offsetEnd[0] - btn / 2,
                                top: cy + w.offsetEnd[1] - btn / 2,
                                width: btn,
                                height: btn,
                                borderRadius: '50%',
                                border: '2px solid #fff',
                                backgroundColor: w.stroke,
                                color: '#fff',
                                fontSize: 12,
                                fontWeight: 'bold',
                                cursor: 'pointer',
                                padding: 0,
                                lineHeight: `${btn - 4}px`,
                            }}
                        >
                            {w.label}
                        </button>
                    )
                })}
            </div>,
            document.body // <--- Portal mounting location target destination
        )
    }

    // Resolve the live streamed value for whichever port this component is
    // plugged into, using the `range<port>` prop-naming convention instead of a
    // per-type ternary ladder. Bare analog/digital ports stream on their first
    // channel (A -> rangeA1), while channel-suffixed and named ports map 1:1
    // (A2 -> rangeA2, servo_motor1 -> rangeservo_motor1). This is what lets the
    // overlay survive on servo_motor1/servo_motor2, where the old branch had no
    // matching case.
    resolveLiveValue = (connectedTo) => {
        if (!connectedTo) return null
        const key = /^[A-G]$/.test(connectedTo)
            ? `${connectedTo}1`
            : connectedTo
        const value = this.props[`range${key}`]
        return value ?? null
    }

    // Render the absolute-positioned live-reading <p> overlay for the current
    // component type, or null when the type isn't a registered streaming sensor.
    // Anchored to the same (left, top) as the component graphic so it overlays it
    // identically in both the dedicated branch and the magnifier branch. zIndex is
    // a parameter because the overlay must out-stack whatever wraps the graphic in
    // that branch: the native '3' clears the bare graphic (zIndex 2), while the
    // magnifier branch passes a higher value to clear its zIndex-10 wrapper.
    renderLiveValueOverlay = (left, top, zIndex = '3') => {
        const overlayStyle = LIVE_VALUE_OVERLAY_STYLES[this.props.type]
        if (!overlayStyle) return null

        return (
            <p
                style={{
                    position: 'absolute',
                    top,
                    left,
                    marginTop: overlayStyle.marginTop,
                    marginLeft: overlayStyle.marginLeft,
                    zIndex,
                    color: '#707070',
                    fontSize: '20px',
                }}
            >
                {this.resolveLiveValue(this.props.connectedTo)}
            </p>
        )
    }

    removeComponent = () => {
        // Fixed kit components (e.g. woofKit's pre-wired servos) can't be
        // disconnected via double-click / long-press. See FixedKitComponents.js.
        if (this.props.fixed) return
        const { type, index } = this.props

        if (HardwareWireConfigs[type] && window.appState) {
            console.log('Resetting magnifier wire state')
            // Cancel any in-progress routing for this component. Per-pin connections
            // live on the instance itself and go away when it is removed below.
            window.appState.setState({
                activeRgbWire: null,
                activeRgbComponent: null,
                rgbMagnifierOpen: false,
            })
        }

        var prev_data = this.props.appState

        var port = this.props.connectedTo
        prev_data.logic.program = removeSensorProgramData(
            prev_data.logic.program,
            port,
            type
        )

        removeHumanoidLocalProgramData(port)
        const isArmShield =
            sessionStorage.getItem('armShield') == 'true' || false
        const isPlayShield = sessionStorage.getItem('shield') == 'true' || false
        const isPeeCeeATR =
            sessionStorage.getItem('peeCeeATR') == 'true' || false
        const connectedDevice = sessionStorage.getItem('connectedDevice')

        if (
            ['Ace', 'Roboki', 'Atr', 'Toki', 'Poki', 'Moki', 'woofKit'].includes(
                sessionStorage.getItem('connectedDevice')
            )
        ) {
            if (type == 'pc_motor_driver') {
                if (port == 'A' || port == 'C') {
                    prev_data.assembly.PortConnections['A'] = null
                    prev_data.assembly.PortConnections['C'] = null
                }
                if (port == 'B' || port == 'D') {
                    prev_data.assembly.PortConnections['B'] = null
                    prev_data.assembly.PortConnections['D'] = null
                }
            } else if (type == 'stepper_motor') {
                if (
                    JSON.parse(sessionStorage.getItem('assembly'))
                        .PortConnections.B !== null
                ) {
                    if (
                        JSON.parse(sessionStorage.getItem('assembly'))
                            .PortConnections.B.type == 'pc_motor_driver'
                    ) {
                        if (port == 'STPM') {
                            prev_data.assembly.PortConnections['B1'] = null
                            prev_data.assembly.PortConnections['B2'] = null
                            prev_data.assembly.PortConnections['D2'] = null
                            prev_data.assembly.PortConnections['D1'] = null
                        }
                    }
                }

                if (
                    JSON.parse(sessionStorage.getItem('assembly'))
                        .PortConnections.A !== null
                ) {
                    if (
                        JSON.parse(sessionStorage.getItem('assembly'))
                            .PortConnections.A.type == 'pc_motor_driver'
                    ) {
                        if (port == 'STPM') {
                            prev_data.assembly.PortConnections['A1'] = null
                            prev_data.assembly.PortConnections['A2'] = null
                            prev_data.assembly.PortConnections['C2'] = null
                            prev_data.assembly.PortConnections['C1'] = null
                        }
                    }
                }
            } else if (
                type != 'servo_motor' ||
                type != 'servo_motor_270' ||
                type != 'ultrasonic_sensor' ||
                type != 'ultrasonic_sensor_v1'
            ) {
                if (port == 'A') {
                    prev_data.assembly.PortConnections['A'] = null
                    prev_data.assembly.PortConnections['A1'] = null
                    prev_data.assembly.PortConnections['A2'] = null
                }
                if (port == 'B') {
                    prev_data.assembly.PortConnections['B'] = null
                    prev_data.assembly.PortConnections['B1'] = null
                    prev_data.assembly.PortConnections['B2'] = null
                }
                if (port == 'C') {
                    prev_data.assembly.PortConnections['C'] = null
                    prev_data.assembly.PortConnections['C1'] = null
                    prev_data.assembly.PortConnections['C2'] = null
                }
                if (port == 'D') {
                    prev_data.assembly.PortConnections['D'] = null
                    prev_data.assembly.PortConnections['D1'] = null
                    prev_data.assembly.PortConnections['D2'] = null
                }
                if (port == 'E') {
                    prev_data.assembly.PortConnections['E'] = null
                    prev_data.assembly.PortConnections['E1'] = null
                    prev_data.assembly.PortConnections['E2'] = null
                }
                if (port == 'F') {
                    prev_data.assembly.PortConnections['F'] = null
                    prev_data.assembly.PortConnections['F1'] = null
                    prev_data.assembly.PortConnections['F2'] = null
                }
            } else {
                if (port == 'A') {
                    prev_data.assembly.PortConnections['A1'] = null
                    prev_data.assembly.PortConnections['A2'] = null
                } else if (port == 'B') {
                    prev_data.assembly.PortConnections['B1'] = null
                    prev_data.assembly.PortConnections['B2'] = null
                } else if (port == 'C') {
                    prev_data.assembly.PortConnections['C1'] = null
                    prev_data.assembly.PortConnections['C2'] = null
                } else if (port == 'D') {
                    prev_data.assembly.PortConnections['D1'] = null
                    prev_data.assembly.PortConnections['D2'] = null
                } else {
                    prev_data.assembly.PortConnections[port] = null
                }
            }
        } else {
            prev_data.assembly.PortConnections[port] = null
        }

        if (port == 'M1' && isPlayShield) {
            prev_data.assembly.PortConnections['M1'] = null
            prev_data.assembly.PortConnections['M2'] = null
        } else if (port == 'M3' && isPlayShield) {
            prev_data.assembly.PortConnections['M3'] = null
            prev_data.assembly.PortConnections['M4'] = null
        } else if (port == 'MOTOR1' && isArmShield) {
            prev_data.assembly.PortConnections['MOTOR1'] = null
            prev_data.assembly.PortConnections['M2'] = null
            prev_data.assembly.PortConnections['M3'] = null
        }

        if (port == 'A1' || port == 'P0') {
            prev_data.assembly.PortConnections['A1'] = null
            prev_data.assembly.PortConnections['P0'] = null
        } else if (port == 'B1' || port == 'P1') {
            prev_data.assembly.PortConnections['B1'] = null
            prev_data.assembly.PortConnections['P1'] = null
        } else if (port == 'C1' || port == 'P2') {
            prev_data.assembly.PortConnections['C1'] = null
            prev_data.assembly.PortConnections['P2'] = null
        }

        if (port == 'S1' && isPcVersion01) {
            prev_data.assembly.PortConnections['A1'] = null
            prev_data.assembly.PortConnections['S1'] = null
        } else if (port == 'S2' && isPcVersion01) {
            prev_data.assembly.PortConnections['C1'] = null
            prev_data.assembly.PortConnections['S2'] = null
        } else if (port == 'S1' && isPcVersion1) {
            prev_data.assembly.PortConnections['S1'] = null
        } else if (port == 'S2' && isPcVersion1) {
            prev_data.assembly.PortConnections['S2'] = null
        } else if (port == 'M1' && isPeeCeeATR) {
            prev_data.assembly.PortConnections['M1'] = null
            prev_data.assembly.PortConnections['M2'] = null
        } else if (port == 'M3' && isPeeCeeATR) {
            prev_data.assembly.PortConnections['M3'] = null
            prev_data.assembly.PortConnections['M4'] = null
        } else if (port == 'G' && isPeeCeeATR && isPcVersion01) {
            prev_data.assembly.PortConnections['G'] = null
            prev_data.assembly.PortConnections['C2'] = null
            prev_data.assembly.PortConnections['F1'] = null
        } else if (port == 'H' && isPeeCeeATR && isPcVersion01) {
            prev_data.assembly.PortConnections['H'] = null
            prev_data.assembly.PortConnections['A2'] = null
            prev_data.assembly.PortConnections['F2'] = null
        } else if (port == 'G' && isPeeCeeATR && isPcVersion1) {
            prev_data.assembly.PortConnections['G'] = null
            prev_data.assembly.PortConnections['E1'] = null
            prev_data.assembly.PortConnections['E2'] = null
        } else if (port == 'H' && isPeeCeeATR && isPcVersion1) {
            prev_data.assembly.PortConnections['H'] = null
            prev_data.assembly.PortConnections['F1'] = null
            prev_data.assembly.PortConnections['F2'] = null
        } else if (port == 'S1' && connectedDevice === 'Atr') {
            prev_data.assembly.PortConnections['C1'] = null
        } else if (port == 'S2' && connectedDevice === 'Atr') {
            prev_data.assembly.PortConnections['C2'] = null
        }

        const joystickCfgOnRemove =
            isJoystickWireType(type) &&
            getJoystickWireConfig(type, connectedDevice, this.props.connectedTo)
        if (joystickCfgOnRemove) {
            // Free every candidate target (not just whichever was actually
            // connected) so a stray claim can't linger under any port group.
            ;['A', 'B'].forEach((wireKey) => {
                joystickCfgOnRemove.wires[wireKey].targets.forEach((target) => {
                    prev_data.assembly.PortConnections[target.port] = null
                    target.occupies.forEach((pin) => {
                        prev_data.assembly.PortConnections[pin] = null
                    })
                })
            })
            const app = window.assemblyAppState
            if (app) {
                const m = app.state.magnifier
                const w = app.state.activeWire
                const patch = {}
                if (m && m.type === type && m.index === index)
                    patch.magnifier = null
                if (w && w.type === type && w.index === index)
                    patch.activeWire = null
                if (Object.keys(patch).length) app.setState(patch)
            }
        }

        this.props.removeFromWorkspace({ type, index, port })

        ////////// manually removing ports while component dropped out of bounds //////
        if (this.props.type == 'dual_splitter') {
            if (this.props.prop.PortConnections['A'] == null) {
                prev_data.assembly.PortConnections['A1'] = null
                prev_data.assembly.PortConnections['A2'] = null
            }
            if (this.props.prop.PortConnections['B'] == null) {
                prev_data.assembly.PortConnections['B1'] = null
                prev_data.assembly.PortConnections['B2'] = null
            }
            if (this.props.prop.PortConnections['C'] == null) {
                prev_data.assembly.PortConnections['C1'] = null
                prev_data.assembly.PortConnections['C2'] = null
            }
            if (this.props.prop.PortConnections['D'] == null) {
                prev_data.assembly.PortConnections['D1'] = null
                prev_data.assembly.PortConnections['D2'] = null
            }
        }
        ////////////////////////////////////////////////////////////////////////////////

        this.props.appState.PortConnections(prev_data.assembly.PortConnections)
        var { logic } = this.props.appState
        this.props.appState.logicComponent(logic)
    }

    typeCheck = () => {
        this.removeComponent()
    }
    render() {
        if (this.props.type == 'rotatory') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            left = left - 10
            top = top - 7

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-1.1%',
                            marginLeft: '1.1%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '0.1%',
                            marginLeft: '6.5%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'pot') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            // laxmi
            left = isDeviceVersion12 ? left - 2 : (left = left + 2)
            top = top - 19

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2.5%',
                            marginLeft: '-1.1%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (this.props.type == 'linear_pot') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            left = left - 10
            top = top + 16

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2.5%',
                            marginLeft: '-3.5%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (['PIR_sensor', 'rain_sensor'].includes(this.props.type)) {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            left = isDeviceVersion12 ? left - 3 : left
            top = top - 21

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top: top + 15,
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                            transform: 'translate(-50%, -50%)',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '4%',
                            marginLeft: '8%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'humidity') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            left = left + 4
            top = top - 9

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-1.1%',
                            marginLeft: '1.1%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'temperature_sensor') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            left = left + 3
            top = top - 18

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2.5%',
                            marginLeft: '-0.5%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (this.props.type == 'gas') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            left = left + 2
            top = top - 21

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2.1%',
                            marginLeft: '-1.1%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (this.props.type == 'distance_sensor') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            left = isDeviceVersion12 ? left - 4 : left - 2
            top = top - 22

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '3%',
                            marginLeft: '-1%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (
            getHardwareWireConfig(
                this.props.type,
                sessionStorage.getItem('connectedDevice')
            ) &&
            ['Toki', 'Poki', 'woofKit'].includes(sessionStorage.getItem('connectedDevice'))
        ) {
            // The 3-wire magnifier flow runs on Toki and Poki (Connections
            // wire/hover engines are gated to ['Toki','Poki'] too; workspace
            // pin-seeding stays Toki-only — Poki seeds from connectedTo). On every
            // other device these same component types (RGB, servo_motor*,
            // ultrasonic_sensor, …) keep their existing dedicated render branches
            // below, so 2-wire behavior is preserved.
            //
            // getHardwareWireConfig honours a config's `devices` allow-list, so a
            // Poki-only component (dc_motor, IMU, OLED, Color_sensor) is invisible
            // here on Toki and falls through to its own branch. Everything else
            // falls through to its single-wire branch (red port highlight).
            var { type, left, top, scale, connectDragSource, isDragging } =
                this.props
            if (isDragging) return null

            const rect = this.rgbContainerRef.current?.getBoundingClientRect()

            var height = 100 * scale
            var width = 100 * scale

            if (
                ['Moki', 'Toki', 'Poki', 'woofKit'].includes(
                    sessionStorage.getItem('connectedDevice')
                )
            ) {
                top = top + 5
            } else {
                left = left + 22
                top = top + 35
            }

            // Safely auto-initialize local state to prevent the "undefined" crash
            if (!this.state) {
                this.state = { rgbMagnifierOpen: false }
            }
            const isMagnifierOpen = this.state.rgbMagnifierOpen

            // Establish the root origin point inside the 150x150 portal
            const originX = 0
            const originY = 0

            // =========================================================
            // CONFIGURING THE DUAL BOARD GLASS PORTAL INDEXERS
            // =========================================================
            const connectedDevice =
                sessionStorage.getItem('connectedDevice') || 'Toki'
            let boardUrl = renderPrgImage('Toki')
            if (connectedDevice === 'Poki') boardUrl = renderPrgImage('pokiBoard')
            if (connectedDevice === 'Moki') boardUrl = renderPrgImage('Moki')
            // woofKit is built on the Poki PCB — use the same assembly board.
            if (connectedDevice === 'woofKit') boardUrl = renderPrgImage('pokiBoard')
            const brandBoardUrl = getConnectedDevicePinout(connectedDevice)
            if (brandBoardUrl) boardUrl = brandBoardUrl

            // Mapping background zoom alignments over Toki structural port headers
            const boardPortPositions = {
                S1: { imageSize: 300, x: 0, y: 10, bgX: 22, bgY: 35 },
                S2: { imageSize: 300, x: 100, y: 10, bgX: 83, bgY: 35 },
                A1: { imageSize: 300, x: 0, y: 90, bgX: 22, bgY: 64 },
                A2: { imageSize: 300, x: 100, y: 90, bgX: 83, bgY: 64 },
            }

            // Determine if the component is currently connected to a specific port
            const rawPortString = this.props.connectedTo
            let activePortKey = null

            if (rawPortString) {
                if (
                    rawPortString.includes('S1') ||
                    rawPortString.includes('motor1')
                )
                    activePortKey = 'S1'
                else if (
                    rawPortString.includes('S2') ||
                    rawPortString.includes('motor2')
                )
                    activePortKey = 'S2'
                else if (rawPortString.includes('A2')) activePortKey = 'A2'
                else if (
                    rawPortString.includes('A1') ||
                    rawPortString.includes('A')
                )
                    activePortKey = 'A1'
            }

            // activePortKey (above) is now used only to highlight the lens of the port
            // this component is already connected to; the board magnifier itself is
            // revealed on hover (see handleBoardPortHover), not on click.

            // Board ports this component's wires are connected to. These get a loupe
            // whenever the component magnifier is open, which covers both:
            //   (1) clicking the component (toggles rgbMagnifierOpen), and
            //   (2) dropping it near a port to auto-connect every pin — a freshly
            //       dropped component mounts with the magnifier open.
            // Derived from the per-pin source of truth, deduped so each distinct
            // target port (e.g. S1 for the signal pin, S2 for +/-) gets its own lens.
            const connectedBoardPorts = isMagnifierOpen
                ? Array.from(
                      new Set(
                          Object.values(
                              (window.appState &&
                                  window.appState.getPinConnections(
                                      this.props.type,
                                      this.props.index
                                  )) ||
                                  {}
                          )
                              .map(mapPortToBoardKey)
                              .filter(Boolean)
                      )
                  )
                : []

            // Live on-screen rect of the board so the magnifier viewports can be anchored
            // to it (and move/zoom with it) instead of sitting at a fixed screen spot.
            // Toki/Atr/Roboki/Moki/Poki all render the board with id="PC_dragSource".
            const boardRect = document
                .getElementById('PC_dragSource')
                ?.getBoundingClientRect()

            return connectDragSource(
                // Outer, unpositioned grouping div mirrors every dedicated sensor
                // branch: it lets the live-value <p> (rendered last, below) share
                // the canvas as its containing block — so its percentage nudges
                // resolve exactly as they do downstream — while the magnifier
                // wrapper keeps its own absolute/zIndex-10 stacking context fully
                // intact. The magnifier internals below are unchanged.
                <div>
                    {/* ADDED THE REF HERE so handleDocumentClick knows what's inside */}
                    <div
                        ref={this.rgbContainerRef}
                        style={{ position: 'absolute', left, top, zIndex: 10 }}
                    >
                        <div
                            id={this.props.type + this.props.index}
                            style={{
                                ...style,
                                left: 0,
                                top: 0,
                                backgroundImage: `url(${renderCompImg(type)})`,
                                height,
                                width,
                            }}
                            onMouseDown={(e) => {
                                // Use a component-specific click timer to bypass DnD interference
                                this.rgbClickTime = Date.now()
                                clickStartTimestamp = Date.now() // Keep original for your long-press delete
                            }}
                            // FIND THIS BLOCK INSIDE: else if (HardwareWireConfigs[this.props.type])
                            onMouseUp={(e) => {
                                this.checkForLongPress()

                                // If it was a quick click (<250ms), toggle the magnifier
                                if (Date.now() - this.rgbClickTime < 250) {
                                    const nextOpenState = !isMagnifierOpen
                                    this.setState({
                                        rgbMagnifierOpen: nextOpenState,
                                        // Clear out manual hovers if closing
                                        hoveredBoardPort: nextOpenState
                                            ? this.state.hoveredBoardPort
                                            : null,
                                    })
                                }
                            }}
                            onDoubleClick={() => this.typeCheck(this)}
                        />

                        {/* The Magnifier POC */}
                        {isMagnifierOpen &&
                            rect &&
                            HardwareWireConfigs[type] &&
                            !MAGNIFIER_EXCLUDED_COMPONENTS.includes(type) &&
                            ReactDOM.createPortal(
                                (() => {
                                    const config = HardwareWireConfigs[type]

                                    // 1. Define the mathematical center point of the pins inside the 150x150 glass circle
                                    const originX = 75 // Horizontal center
                                    const originY = 75 // Vertical start point (where pins emerge from the component)

                                    // Per-pin connection state for this instance, so we can
                                    // draw an extra wire below only for pins that are wired.
                                    let pinConnections =
                                        (window.appState &&
                                            window.appState.getPinConnections(
                                                this.props.type,
                                                this.props.index
                                            )) ||
                                        {}
                                    // Drag-dropping a component on its port (notably on Poki)
                                    // wires every pin but only sets `connectedTo` — it does not
                                    // seed the per-pin map (that seeding is Toki-only). Mirror
                                    // the Connections.js hint fallback so the "connected"
                                    // indicator wire shows then too: an empty map with a known
                                    // connectedTo means every pin is wired to that port.
                                    if (
                                        Object.keys(pinConnections).length ===
                                            0 &&
                                        this.props.connectedTo
                                    ) {
                                        const fallbackPins = {}
                                        Object.keys(config.pins).forEach(
                                            (pk) => {
                                                fallbackPins[pk] =
                                                    this.props.connectedTo
                                            }
                                        )
                                        pinConnections = fallbackPins
                                    }
                                    // Vertical gap (px) between the original wire and the
                                    // duplicate drawn just below it for connected pins.
                                    const CONNECTED_WIRE_GAP = 40

                                    return (
                                        <div
                                            style={{
                                                position: 'fixed',
                                                top: rect.top + 60,
                                                left: rect.left + 50,
                                                width: '150px',
                                                height: '150px',
                                                borderRadius: '50%',
                                                backgroundImage: `url(${renderCompImg(type)})`,
                                                backgroundRepeat: 'no-repeat',
                                                backgroundSize: `${config.imageSize}%`,
                                                backgroundPosition: `${config.imageX}% ${config.imageY}%`,
                                                backgroundColor:
                                                    'rgba(255, 255, 255, 0.4)',
                                                backdropFilter: 'blur(6px)',
                                                WebkitBackdropFilter:
                                                    'blur(6px)',
                                                border: '3px solid rgba(199, 199, 199, 0.6)',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.2), inset 0 0 15px rgba(255,255,255,0.3)',
                                                zIndex: 99999,
                                                overflow: 'hidden',
                                            }}
                                        >
                                            {/* 2. SVG now covers the full 150x150 circle. 1 pixel in SVG = 1 pixel of left/top */}
                                            <svg
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                {Object.entries(
                                                    config.pins
                                                ).map(([pinKey, pinMeta]) => (
                                                    <React.Fragment
                                                        key={`line-group-${pinKey}`}
                                                    >
                                                        <line
                                                            x1={
                                                                originX +
                                                                pinMeta
                                                                    .offsetStart[0]
                                                            }
                                                            y1={
                                                                originY +
                                                                pinMeta
                                                                    .offsetStart[1]
                                                            }
                                                            x2={
                                                                originX +
                                                                pinMeta
                                                                    .offsetEnd[0]
                                                            }
                                                            y2={
                                                                originY +
                                                                pinMeta
                                                                    .offsetEnd[1]
                                                            }
                                                            stroke={
                                                                pinMeta.stroke
                                                            }
                                                            strokeWidth="3"
                                                            strokeLinecap="round"
                                                        />
                                                        {/* Extra wire just below, only for pins that are connected */}
                                                        {pinConnections[
                                                            pinKey
                                                        ] && (
                                                            <line
                                                                x1={
                                                                    originX +
                                                                    pinMeta
                                                                        .offsetEnd[0]
                                                                }
                                                                y1={
                                                                    originY +
                                                                    pinMeta
                                                                        .offsetEnd[1]
                                                                    // CONNECTED_WIRE_GAP
                                                                }
                                                                x2={
                                                                    originX +
                                                                    pinMeta
                                                                        .offsetEnd[0]
                                                                }
                                                                y2={
                                                                    originY +
                                                                    pinMeta
                                                                        .offsetEnd[1] +
                                                                    CONNECTED_WIRE_GAP
                                                                }
                                                                stroke={
                                                                    pinMeta.stroke
                                                                }
                                                                strokeWidth="3"
                                                                strokeLinecap="round"
                                                            />
                                                        )}
                                                    </React.Fragment>
                                                ))}
                                            </svg>

                                            {/* 3. Buttons map directly to the end of the lines (x2, y2) */}
                                            {Object.entries(config.pins).map(
                                                ([pinKey, pinMeta]) => {
                                                    // Subtract 9px to center the 18px wide/high button exactly over the line tip
                                                    const btnX =
                                                        originX +
                                                        pinMeta.offsetEnd[0] -
                                                        9
                                                    const btnY =
                                                        originY +
                                                        pinMeta.offsetEnd[1] -
                                                        9

                                                    return (
                                                        <button
                                                            key={`btn-${pinKey}`}
                                                            onMouseDown={(
                                                                e
                                                            ) => {
                                                                e.stopPropagation()
                                                                this.setState({
                                                                    rgbMagnifierOpen:
                                                                        !this
                                                                            .state
                                                                            .rgbMagnifierOpen,
                                                                })

                                                                if (
                                                                    window.appState &&
                                                                    window
                                                                        .appState
                                                                        .startPinWire
                                                                ) {
                                                                    // Poki: this pin's hint persists like a wire.
                                                                    // Re-clicking the SAME pin button while its
                                                                    // wire is still being routed (not yet dropped
                                                                    // on a port) removes it (toggle off). If the
                                                                    // pin is already connected, fall through to
                                                                    // startPinWire so it re-routes and the wire
                                                                    // follows the cursor again — unchanged behavior.
                                                                    const isPoki =
                                                                        [
                                                                            'Poki',
                                                                            'woofKit',
                                                                        ].includes(
                                                                            sessionStorage.getItem(
                                                                                'connectedDevice'
                                                                            )
                                                                        )
                                                                    const s =
                                                                        window
                                                                            .appState
                                                                            .state
                                                                    const pc =
                                                                        (window
                                                                            .appState
                                                                            .getPinConnections &&
                                                                            window.appState.getPinConnections(
                                                                                this
                                                                                    .props
                                                                                    .type,
                                                                                this
                                                                                    .props
                                                                                    .index
                                                                            )) ||
                                                                        {}
                                                                    if (
                                                                        isPoki &&
                                                                        s.activeRgbWire ===
                                                                            pinKey &&
                                                                        s
                                                                            .activeRgbComponent
                                                                            ?.type ===
                                                                            this
                                                                                .props
                                                                                .type &&
                                                                        s
                                                                            .activeRgbComponent
                                                                            ?.index ===
                                                                            this
                                                                                .props
                                                                                .index &&
                                                                        !pc[
                                                                            pinKey
                                                                        ]
                                                                    ) {
                                                                        window.appState.setState(
                                                                            {
                                                                                activeRgbWire:
                                                                                    null,
                                                                                activeRgbComponent:
                                                                                    null,
                                                                            }
                                                                        )
                                                                    } else {
                                                                        window.appState.startPinWire(
                                                                            this
                                                                                .props
                                                                                .type,
                                                                            this
                                                                                .props
                                                                                .index,
                                                                            pinKey
                                                                        )
                                                                    }
                                                                }
                                                            }}
                                                            onClick={(e) =>
                                                                e.stopPropagation()
                                                            }
                                                            className="wire-pin-button"
                                                            style={{
                                                                left: `${btnX}px`,
                                                                top: `${btnY}px`,
                                                                background:
                                                                    pinMeta.stroke,
                                                            }}
                                                        >
                                                            {pinKey === '+' ||
                                                            pinKey === '-'
                                                                ? pinKey
                                                                : pinMeta.label ||
                                                                  pinKey.charAt(
                                                                      0
                                                                  )}
                                                        </button>
                                                    )
                                                }
                                            )}
                                        </div>
                                    )
                                })(),
                                document.body
                            )}

                        {/* Toki Board Port Magnifier Loupe — revealed only while the cursor
                        hovers near a port (see handleBoardPortHover). It is a LIVE loupe:
                        it re-renders the board image and references the live wire SVG group
                        (<use href="#assembly-wire-camera-g">), then scales/translates the
                        whole thing so the hovered port sits at the lens centre. Because the
                        wires are referenced (not snapshotted), anything drawn there —
                        including a wire being dragged toward the port — appears zoomed in
                        real time. */}
                        {HardwareWireConfigs[type] &&
                            this.state &&
                            boardRect &&
                            (() => {
                                // Render a loupe for the hovered port (if any) plus every
                                // port the component is wired to (while its magnifier is
                                // open). Deduped so a port that is both hovered and
                                // connected only gets one lens.
                                const portsToShow = Array.from(
                                    new Set(
                                        [
                                            this.state.hoveredBoardPort,
                                            ...connectedBoardPorts,
                                        ].filter(Boolean)
                                    )
                                )
                                if (portsToShow.length === 0) return null

                                return portsToShow.map((portKey) => {
                                    const anchor = BOARD_PORT_POSITIONS[portKey]
                                    if (!anchor) return null

                                    const LENS_SIZE = 150
                                    const LOUPE_ZOOM = 2.0 // magnification factor

                                    // The board-image port anchors (BOARD_PORT_POSITIONS,
                                    // hand-tuned % of the board rect) and the live wire
                                    // endpoints (drawn from world coords via the camera
                                    // matrix in Connections.js) are two independent
                                    // representations of the same pads, and they disagree by
                                    // a small, position-dependent amount: the wire layer is
                                    // very slightly compressed toward the board's horizontal
                                    // centre relative to the image. At 1x it's invisible, but
                                    // the 2x loupe magnifies it into a ~10-20px gap — the wire
                                    // reads as drifting toward centre (left ports' wires lean
                                    // right, right ports' wires lean left). We undo that
                                    // compression locally by nudging the wire copy back out
                                    // from the board centre, proportional to the port's
                                    // distance from centre (so a centred port is untouched and
                                    // edge ports get the full correction). Tune this single
                                    // fraction visually if the wires need more / less nudge.
                                    const WIRE_CENTER_COMPRESSION = 0.14

                                    // Where the lens SITS on screen (x/y anchor).
                                    const lensCenterX =
                                        boardRect.left +
                                        (anchor.x / 100) * boardRect.width
                                    const lensCenterY =
                                        boardRect.top +
                                        (anchor.y / 100) * boardRect.height

                                    const lensLeft = lensCenterX - LENS_SIZE / 2
                                    const lensTop = lensCenterY - LENS_SIZE / 2

                                    // Which area the lens MAGNIFIES (focusX/Y, falling back to x/y).
                                    // Decoupled from the lens position so you can show a different
                                    // area than where the lens sits.
                                    const focusX =
                                        boardRect.left +
                                        ((anchor.hoverX ?? anchor.x) / 100) *
                                            boardRect.width
                                    const focusY =
                                        boardRect.top +
                                        ((anchor.hoverY ?? anchor.y) / 100) *
                                            boardRect.height

                                    // Map screen-space → lens-local: point * ZOOM + T must put the
                                    // focus point at the lens centre (LENS_SIZE / 2).
                                    const Tx =
                                        LENS_SIZE / 2 - focusX * LOUPE_ZOOM
                                    const Ty =
                                        LENS_SIZE / 2 - focusY * LOUPE_ZOOM

                                    // Push the wire copy back outward from the board centre to
                                    // cancel the wire-vs-image compression (see comment above).
                                    // Screen-space px; applied only to the wire <g>, never the
                                    // board image, so the wire lands back on its pad. Horizontal
                                    // only — the reported drift is left/right, so we leave Y
                                    // untouched to avoid introducing a vertical shift.
                                    const boardCenterX =
                                        boardRect.left + boardRect.width / 2
                                    const wireShiftX =
                                        (focusX - boardCenterX) *
                                        WIRE_CENTER_COMPRESSION

                                    // The live wire SVG draws in #connectPort-internal coords; its
                                    // rect's top-left is where internal (0,0) lands on screen, so we
                                    // offset the <use> copy by it to put wires at true screen coords.
                                    const connectRect = document
                                        .getElementById('connectPort')
                                        ?.getBoundingClientRect()

                                    // === Toki magnifying-glass board overlay ========
                                    // Purely visual / non-interactive. Draws a
                                    // translucent "lens footprint" on the board over the
                                    // exact area this loupe magnifies, plus two connector
                                    // lines linking that footprint to the floating lens —
                                    // the frame of a handheld magnifying glass. Toki-only;
                                    // Poki keeps its plain magnifier untouched.
                                    let boardGlassOverlay = null
                                    if (connectedDevice === 'Toki') {
                                        const isActive =
                                            activePortKey === portKey
                                        // The lens reveals a board circle of this radius
                                        // (lens radius ÷ zoom) centred on the focus point,
                                        // so this matches the magnified area exactly.
                                        const lensRadius = LENS_SIZE / 2
                                        const focusRadius =
                                            lensRadius / LOUPE_ZOOM

                                        // External tangents between the small footprint and
                                        // the large lens give the two sides of the frame.
                                        const dx = lensCenterX - focusX
                                        const dy = lensCenterY - focusY
                                        const dist = Math.hypot(dx, dy) || 1
                                        const gamma = Math.atan2(dy, dx)
                                        const cosBeta = Math.max(
                                            -1,
                                            Math.min(
                                                1,
                                                (lensRadius - focusRadius) /
                                                    dist
                                            )
                                        )
                                        const beta = Math.acos(cosBeta)
                                        const tangent = (cx, cy, r, sign) => ({
                                            x:
                                                cx +
                                                r *
                                                    Math.cos(
                                                        gamma + sign * beta
                                                    ),
                                            y:
                                                cy +
                                                r *
                                                    Math.sin(
                                                        gamma + sign * beta
                                                    ),
                                        })
                                        const lensA = tangent(
                                            lensCenterX,
                                            lensCenterY,
                                            lensRadius,
                                            +1
                                        )
                                        const lensB = tangent(
                                            lensCenterX,
                                            lensCenterY,
                                            lensRadius,
                                            -1
                                        )
                                        const focA = tangent(
                                            focusX,
                                            focusY,
                                            focusRadius,
                                            +1
                                        )
                                        const focB = tangent(
                                            focusX,
                                            focusY,
                                            focusRadius,
                                            -1
                                        )

                                        // Subtle tint matched to the lens border: a soft
                                        // green for the connected port, soft blue otherwise.
                                        const tint = isActive
                                            ? '145, 209, 145'
                                            : '135, 206, 250'

                                        boardGlassOverlay =
                                            ReactDOM.createPortal(
                                                <svg
                                                    key={`board-glass-${portKey}`}
                                                    className="toki-mag-glass"
                                                    style={{
                                                        position: 'fixed',
                                                        top: 0,
                                                        left: 0,
                                                        width: '100vw',
                                                        height: '100vh',
                                                        overflow: 'visible',
                                                        pointerEvents: 'none',
                                                        zIndex: 99998,
                                                    }}
                                                >
                                                    {/* Connector frame — drawn first so the
                                            footprint sits on top of the line ends. */}
                                                    <line
                                                        x1={focA.x}
                                                        y1={focA.y}
                                                        x2={lensA.x}
                                                        y2={lensA.y}
                                                        stroke={`rgba(${tint}, 0.35)`}
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                    <line
                                                        x1={focB.x}
                                                        y1={focB.y}
                                                        x2={lensB.x}
                                                        y2={lensB.y}
                                                        stroke={`rgba(${tint}, 0.35)`}
                                                        strokeWidth="2"
                                                        strokeLinecap="round"
                                                    />
                                                    {/* Translucent lens footprint on the board */}
                                                    <circle
                                                        cx={focusX}
                                                        cy={focusY}
                                                        r={focusRadius}
                                                        fill={`rgba(${tint}, 0.12)`}
                                                        stroke={`rgba(${tint}, 0.55)`}
                                                        strokeWidth="1.5"
                                                        style={{
                                                            filter: 'drop-shadow(0 2px 6px rgba(0,0,0,0.25))',
                                                        }}
                                                    />
                                                    {/* Faint inner ring for a glassy edge */}
                                                    <circle
                                                        cx={focusX}
                                                        cy={focusY}
                                                        r={Math.max(
                                                            0,
                                                            focusRadius - 3
                                                        )}
                                                        fill="none"
                                                        stroke="rgba(255, 255, 255, 0.35)"
                                                        strokeWidth="1"
                                                    />
                                                </svg>,
                                                document.body
                                            )
                                    }

                                    const lensPortal = ReactDOM.createPortal(
                                        <div
                                            key={`board-mag-${portKey}`}
                                            style={{
                                                position: 'fixed',
                                                top: `${lensTop}px`,
                                                left: `${lensLeft}px`,
                                                width: `${LENS_SIZE}px`,
                                                height: `${LENS_SIZE}px`,
                                                borderRadius: '50%',
                                                overflow: 'hidden',
                                                background:
                                                    'rgba(255, 255, 255, 0.9)',
                                                border:
                                                    activePortKey === portKey
                                                        ? '4px solid rgba(145, 209, 145, 0.9)'
                                                        : '3px solid rgba(135, 206, 250, 0.8)',
                                                boxShadow:
                                                    '0 10px 30px rgba(0,0,0,0.2), inset 0 0 15px rgba(255,255,255,0.3)',
                                                zIndex: 99999,
                                            }}
                                        >
                                            {/* Zoomed live view: children are placed at true screen
                                        coordinates, then this wrapper scales/translates them
                                        so the port lands at the lens centre. */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: `${window.innerWidth}px`,
                                                    height: `${window.innerHeight}px`,
                                                    transformOrigin: '0 0',
                                                    transform: `translate(${Tx}px, ${Ty}px) scale(${LOUPE_ZOOM})`,
                                                    pointerEvents: 'none',
                                                }}
                                            >
                                                {/* Live copy of the board image at its real position */}
                                                <img
                                                    src={boardUrl}
                                                    style={{
                                                        position: 'absolute',
                                                        left: `${boardRect.left}px`,
                                                        top: `${boardRect.top}px`,
                                                        width: `${boardRect.width}px`,
                                                        height: `${boardRect.height}px`,
                                                        objectFit: 'contain',
                                                        objectPosition:
                                                            brandBoardUrl
                                                                ? 'center'
                                                                : getDeviceImagePosition(
                                                                      connectedDevice,
                                                                      boardRect.width,
                                                                      boardRect.height,
                                                                      boardRect.height /
                                                                          BOARD_IMAGE_HEIGHT
                                                                  ),
                                                    }}
                                                />
                                                {/* Live copy of the wires via SVG <use> */}
                                                {connectRect && (
                                                    <svg
                                                        style={{
                                                            position:
                                                                'absolute',
                                                            left: 0,
                                                            top: 0,
                                                            width: `${window.innerWidth}px`,
                                                            height: `${window.innerHeight}px`,
                                                            overflow: 'visible',
                                                        }}
                                                    >
                                                        <g
                                                            transform={`translate(${connectRect.left + wireShiftX}, ${connectRect.top})`}
                                                        >
                                                            <use href="#assembly-wire-camera-g" />
                                                        </g>
                                                    </svg>
                                                )}
                                            </div>

                                            {/* Floating port label — kept outside the zoom wrapper
                                        so it stays a fixed size. */}
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    top: '8px',
                                                    left: '50%',
                                                    transform:
                                                        'translateX(-50%)',
                                                    background:
                                                        activePortKey ===
                                                        portKey
                                                            ? 'rgba(145, 209, 145, 0.9)'
                                                            : 'rgba(34, 34, 34, 0.75)',
                                                    color: '#ffffff',
                                                    padding: '2px 8px',
                                                    borderRadius: '10px',
                                                    fontSize: '11px',
                                                    fontWeight: 'bold',
                                                    textTransform: 'uppercase',
                                                    pointerEvents: 'none',
                                                    letterSpacing: '0.5px',
                                                }}
                                            >
                                                Port {portKey}
                                            </div>
                                        </div>,
                                        document.body
                                    )

                                    return (
                                        <React.Fragment
                                            key={`board-mag-wrap-${portKey}`}
                                        >
                                            {boardGlassOverlay}
                                            {lensPortal}
                                        </React.Fragment>
                                    )
                                })
                            })()}
                    </div>

                    {/* Native live-reading overlay, restored for the magnifier
                        path. Anchored to the same (left, top) as the component
                        graphic and given a zIndex above the wrapper so it stays
                        legible on every port — including servo_motor1/2. Renders
                        null for non-streaming types (RGB, motors, OLED, …). */}
                    {this.renderLiveValueOverlay(left, top, 11)}
                </div>
            )
        } else if (this.props.type == 'RGB') {
            // Non-Toki RGB rendering (preserved original behavior). On Toki the
            // branch above handles RGB via the 3-wire magnifier flow instead.
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }
            const device = sessionStorage.getItem('connectedDevice')
            if (['Moki', 'Toki', 'Poki', 'woofKit'].includes(device)) {
                var height = 100 * scale
                var width = 100 * scale
                left = left
                top = top + 5
            } else if (device === 'Roboki') {
                var height = 100 * scale
                var width = 100 * scale
                left = left + 22
                top = top + 35
            } else {
                var height = 100 * scale
                var width = 100 * scale
                left = left + 22
                top = top + 35
            }

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                </div>
            )
        } else if (this.props.type == 'mp3') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            left = left + 13
            top = top + 14

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                </div>
            )
        } else if (this.props.type == 'tact_switch') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            left = left + 4
            top = top - 13

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-0.5%',
                            marginLeft: '0.5%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (this.props.type == 'tact_switch_2c') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            top = isDeviceVersion12 ? top - 10 : top - 5
            left = isDeviceVersion12 ? left - 1 : left - 5

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-0.5%',
                            marginLeft: '0.5%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-0.1%',
                            marginLeft: '6%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'dip_switch') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            top = isDeviceVersion12 ? top - 20 : top - 19
            left = isDeviceVersion12 ? left - 2 : left + 3

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-1.2%',
                            marginLeft: '3%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-0.7%',
                            marginLeft: '6.5%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'metal_detector') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 150 * scale
            var width = 150 * scale
            top = top - 60
            left = left - 11

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-0.5%',
                            marginLeft: '0.5%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo == 'A2'
                              ? this.props.rangeA2
                              : this.props.connectedTo === 'B' ||
                                  this.props.connectedTo === 'B1'
                                ? this.props.rangeB1
                                : this.props.connectedTo == 'B2'
                                  ? this.props.rangeB2
                                  : this.props.connectedTo === 'C' ||
                                      this.props.connectedTo === 'C1'
                                    ? this.props.rangeC1
                                    : this.props.connectedTo == 'C2'
                                      ? this.props.rangeC2
                                      : this.props.connectedTo === 'D' ||
                                          this.props.connectedTo === 'D1'
                                        ? this.props.rangeD1
                                        : this.props.connectedTo == 'D2'
                                          ? this.props.rangeD2
                                          : this.props.connectedTo === 'E' ||
                                              this.props.connectedTo === 'E1'
                                            ? this.props.rangeE1
                                            : this.props.connectedTo == 'E2'
                                              ? this.props.rangeE2
                                              : this.props.connectedTo ===
                                                      'F' ||
                                                  this.props.connectedTo ===
                                                      'F1'
                                                ? this.props.rangeF1
                                                : this.props.connectedTo == 'F2'
                                                  ? this.props.rangeF2
                                                  : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'dual_splitter') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = isDeviceVersion12 ? (5 + 140) * scale : 140 * scale
            var width = isDeviceVersion12 ? (20 + 130) * scale : 130 * scale
            top = top - 22
            left = left - 21

            return connectDragSource(
                <div
                    id={this.props.type + this.props.index}
                    style={{
                        ...style,
                        left,
                        top,
                        backgroundImage: `url(${renderCompImg(type)})`,
                        height,
                        width,
                    }}
                    onMouseDown={() => {
                        clickStartTimestamp = Date.now()
                    }}
                    onMouseUp={this.checkForLongPress}
                    onDoubleClick={() => this.typeCheck(this)}
                />
            )
        } else if (
            this.props.type == 'dc_motor' ||
            this.props.type == 'dynamex_motor'
        ) {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 120 * scale
            var width = 120 * scale
            if (this.props.type == 'dynamex_motor') {
                top = top + 10
                left = left + 10
            } else {
                top = top - 7
                left = left - 10
            }

            return connectDragSource(
                <div
                    id={this.props.type + this.props.index}
                    style={{
                        ...style,
                        left,
                        top,
                        backgroundImage: `url(${renderCompImg(type)})`,
                        height,
                        width,
                    }}
                    onMouseDown={() => {
                        clickStartTimestamp = Date.now()
                    }}
                    onMouseUp={this.checkForLongPress}
                    onDoubleClick={() => this.typeCheck(this)}
                />
            )
        } else if (this.props.type == 'mini_geared_motor') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 120 * scale
            var width = 120 * scale
            left = left - 15
            top = top - 12

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                </div>
            )
        } else if (this.props.type == 'geared_motor') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 120 * scale
            var width = 120 * scale
            left = left - 7
            top = top - 8

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                </div>
            )
        } else if (this.props.type == 'joystick') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 120 * scale
            var width = 120 * scale
            left = isDeviceVersion12 ? left - 15 : left - 7
            top = isDeviceVersion12 ? top - 10 : top - 29

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2%',
                            marginLeft: '-0.5%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2%',
                            marginLeft: '7%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (
            this.props.type === 'joystick_v2' ||
            (this.props.type === 'joystick' &&
                sessionStorage.getItem('connectedDevice') === 'Humanoid')
        ) {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 120 * scale
            var width = 120 * scale
            left = isDeviceVersion12 ? left - 15 : left - 7
            top = isDeviceVersion12 ? top - 10 : top - 29

            const rangeByPort = {
                A1: this.props.rangeA1,
                A2: this.props.rangeA2,
                B1: this.props.rangeB1,
                B2: this.props.rangeB2,
                C1: this.props.rangeC1,
                C2: this.props.rangeC2,
                D1: this.props.rangeD1,
                D2: this.props.rangeD2,
            }
            const wc = this.props.wireConnections || {}
            const analogPort = wc.A
            const digitalPort = wc.B
            const joyV2_x = analogPort ? rangeByPort[analogPort + '1'] : null
            const joyV2_y = analogPort ? rangeByPort[analogPort + '2'] : null
            const joyV2_btn = digitalPort
                ? rangeByPort[digitalPort + '1']
                : null

            return connectDragSource(
                <div data-joystick-ui="1">
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    {(this.props.isReading || this.props.isPcActive) && (
                        <>
                            <div
                                style={{
                                    position: 'absolute',
                                    left: left - 32,
                                    top: top - 14,
                                    zIndex: 3,
                                    color: '#707070',
                                    fontSize: '18px',
                                    lineHeight: '1.2',
                                    pointerEvents: 'none',
                                }}
                            >
                                <p style={{ margin: 0 }}>
                                    x: {joyV2_x !== null ? joyV2_x : '-'}
                                </p>
                                <p style={{ margin: 0 }}>
                                    y: {joyV2_y !== null ? joyV2_y : '-'}
                                </p>
                            </div>
                            <div
                                style={{
                                    position: 'absolute',
                                    left: left + width - 48,
                                    top: top + 9,
                                    zIndex: 3,
                                    color: '#707070',
                                    fontSize: '18px',
                                    lineHeight: '1.2',
                                    pointerEvents: 'none',
                                    textAlign: 'right',
                                }}
                            >
                                <p style={{ margin: 0 }}>
                                    B: {joyV2_btn !== null ? joyV2_btn : '-'}
                                </p>
                            </div>
                        </>
                    )}
                    {this.renderJoystickMagnifier(left, top, width)}
                </div>
            )
        } else if (this.props.type == 'extender') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            left = left
            top = top - 19

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2%',
                            marginLeft: '-0.5%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2%',
                            marginLeft: '7%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (
            this.props.type == 'ultrasonic_sensor' ||
            this.props.type == 'ultrasonic_sensor_v1'
        ) {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            left = left - 10

            if (
                ['Poki', 'Moki', 'woofKit'].includes(
                    sessionStorage.getItem('connectedDevice')
                )
            ) {
                top = top + 17
            } else {
                top = top + 12
            }

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-1.1%',
                            marginLeft: '1.1%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (this.props.type == 'light_sensor') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            left = isDeviceVersion12 ? left + 1 : left + 3
            top = top - 13

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '3%',
                            marginLeft: '-2%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (this.props.type == 'touch_sensor') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 110 * scale
            var width = 110 * scale
            left = left + 2
            top = top - 19

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '-1%',
                            marginLeft: '0%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.resolveLiveValue(this.props.connectedTo)}
                    </p>
                </div>
            )
        } else if (this.props.type == 'dual_switch') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 120 * scale
            var width = 120 * scale
            top = isDeviceVersion12 ? top - 30 : top - 27
            left = isDeviceVersion12 ? left - 5 : left - 2

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '3.5%',
                            marginLeft: '1%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '4%',
                            marginLeft: '8%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'octa_splitter') {
            let {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 125 * scale
            var width = 125 * scale

            if (height == 115 || height == 95.83333333333334) {
            } else {
                top = top - 40
                left = left - 10
            }

            return connectDragSource(
                <div
                    id={this.props.type + this.props.index}
                    style={{
                        ...style,
                        left,
                        top,
                        height,
                        width,
                    }}
                    onMouseDown={() => {
                        clickStartTimestamp = Date.now()
                    }}
                    onMouseUp={this.checkForLongPress}
                    onDoubleClick={() => this.typeCheck(this)}
                />
            )
        } else if (this.props.type == 'temp_gas') {
            const {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2%',
                            marginLeft: '-2%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2%',
                            marginLeft: '7%',
                            left,
                            zIndex: '3',
                            fontSize: '20px',
                            color: '#707070',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (this.props.type == 'temp_dew') {
            const {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2.5%',
                            marginLeft: '-2.5%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A1'
                            ? this.props.rangeA1
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B1'
                              ? this.props.rangeB1
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C1'
                                ? this.props.rangeC1
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D1'
                                  ? this.props.rangeD1
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E1'
                                    ? this.props.rangeE1
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F1'
                                      ? this.props.rangeF1
                                      : null}
                    </p>
                    <p
                        style={{
                            position: 'absolute',
                            top,
                            marginTop: '2.5%',
                            marginLeft: '6.5%',
                            left,
                            zIndex: '3',
                            color: '#707070',
                            fontSize: '20px',
                        }}
                    >
                        {this.props.connectedTo === 'A' ||
                        this.props.connectedTo === 'A2'
                            ? this.props.rangeA2
                            : this.props.connectedTo === 'B' ||
                                this.props.connectedTo === 'B2'
                              ? this.props.rangeB2
                              : this.props.connectedTo === 'C' ||
                                  this.props.connectedTo === 'C2'
                                ? this.props.rangeC2
                                : this.props.connectedTo === 'D' ||
                                    this.props.connectedTo === 'D2'
                                  ? this.props.rangeD2
                                  : this.props.connectedTo === 'E' ||
                                      this.props.connectedTo === 'E2'
                                    ? this.props.rangeE2
                                    : this.props.connectedTo === 'F' ||
                                        this.props.connectedTo === 'F2'
                                      ? this.props.rangeF2
                                      : null}
                    </p>
                </div>
            )
        } else if (
            this.props.type == 'servo_motor' ||
            this.props.type == 'servo_motor_270' ||
            this.props.type == 'servo_motor_sg90'
        ) {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            top = top + 10
            left = left

            return connectDragSource(
                <div
                    id={this.props.type + this.props.index}
                    style={{
                        ...style,
                        left,
                        top,
                        backgroundImage: `url(${renderCompImg(type)})`,
                        height,
                        width,
                    }}
                    onMouseDown={() => {
                        clickStartTimestamp = Date.now()
                    }}
                    onMouseUp={this.checkForLongPress}
                    onDoubleClick={() => this.typeCheck(this)}
                />
            )
        } else if (this.props.type == 'servo_motor_360') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            top = top + 10
            left = left

            return connectDragSource(
                <div
                    id={this.props.type + this.props.index}
                    style={{
                        ...style,
                        left,
                        top,
                        backgroundImage: `url(${renderCompImg(type)})`,
                        height,
                        width,
                    }}
                    onMouseDown={() => {
                        clickStartTimestamp = Date.now()
                    }}
                    onMouseUp={this.checkForLongPress}
                    onDoubleClick={() => this.typeCheck(this)}
                />
            )
        } else if (this.props.type == 'OLED') {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 105 * scale
            var width = 105 * scale
            top = top + 6
            left = left - 10

            return connectDragSource(
                <div
                    id={this.props.type + this.props.index}
                    style={{
                        ...style,
                        left,
                        top,
                        backgroundImage: `url(${renderCompImg(type)})`,
                        height,
                        width,
                    }}
                    onMouseDown={() => {
                        clickStartTimestamp = Date.now()
                    }}
                    onMouseUp={this.checkForLongPress}
                    onDoubleClick={() => this.typeCheck(this)}
                />
            )
        } else if (
            this.props.type == 'play_shield' ||
            this.props.type == 'play_shield_v1'
        ) {
            sessionStorage.setItem('shield', 'true')
            this.props.setShield(true)
            return null
        } else if (this.props.type == 'arm_shield') {
            sessionStorage.setItem('armShield', 'true')
            this.props.setArmShield(true)
            return null
        } else if (this.props.type == 'peeCee_ATR') {
            sessionStorage.setItem('peeCeeATR', 'true')
            this.props.setPeeCeeATR(true)
            return null
        } else if (this.props.type == 'pcAiTool') {
            const {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }
            let isAi_lidarSelect =
                this.props.appState?.assembly?.assemblyPageAi?.aiItems
                    ?.ai_lidar ||
                this.props.appState?.assembly?.assemblyPageAi?.aiItems
                    ?.ai_proximity ||
                this.props.appState?.assembly?.assemblyPageAi?.aiItems?.ai_light

            var height = 100 * scale
            var width = 100 * scale
            let pTagTop = '10px'

            if (
                [
                    'Analog_3_Pin_Input',
                    'Analog_2_Pin_Input',
                    'PWM_3_Pin_Output',
                    'PWM_2_Pin_Output',
                    'Digital_3_Pin_Input',
                    'Digital_2_Pin_Input',
                    'Digital_3_Pin_Output',
                    'Digital_2_Pin_Output',
                ].includes(this.props.type)
            ) {
                left = left + 2
                top = top + 10
            } else if (
                ['DHT11', 'sound_sensor', 'IR_proximity'].includes(
                    this.props.type
                )
            ) {
                left = left + 5
                top = top + 15
            } else if (this.props.type == 'L293D_motor_driver') {
                left = left + 5
                top = top + 25
            } else if (this.props.type == 'LDR_sensor') {
                left = left + 2
                top = top + 2
                pTagTop = '40px'
            }

            const isAiSelectOpen = !!this.props.appState?.assembly
                ?.assemblyPageAi?.isShowAiSelectContainer

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left: left + 3,
                            top: top - 12,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onClick={(e) => {
                            clearTimeout(this.handleClickTimeout)
                            this.handleClickTimeout = setTimeout(() => {
                                // PA Read and AI-1 V1 settings are mutually exclusive;
                                // warn instead of silently ignoring the click.
                                if (this.props.isPcActive || this.props.isReading) {
                                    window.appState?.showAiModelPopUp?.({
                                        elementKey:
                                            'Disable PA Read before enabling AI-1 V1 settings',
                                        text: true,
                                        time: 4000,
                                    })
                                    return
                                }
                                if (
                                    !this.props.aiVersion &&
                                    this.props.webSerial?.isConnected
                                ) {
                                    return
                                }
                                let assemblyPageAi = {
                                    ...this.props.appState.assembly
                                        .assemblyPageAi,
                                }
                                assemblyPageAi.isShowAiSelectContainer = true
                                this.props.setAssemblyPageAi(assemblyPageAi)
                            }, 300)
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => {
                            clearTimeout(this.handleClickTimeout)
                            this.typeCheck(this)
                        }}
                    />
                    <button
                        type="button"
                        className={
                            'ai-mode-settings-button' +
                            (isAiSelectOpen ? ' ai-mode-settings-button--active' : '')
                        }
                        title="AI mode settings"
                        aria-label={
                            isAiSelectOpen
                                ? 'Close AI mode settings'
                                : 'Open AI mode settings'
                        }
                        aria-pressed={isAiSelectOpen}
                        style={{
                            left: left + width - 8,
                            top: top - 34,
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => {
                            e.stopPropagation()
                            clearTimeout(this.handleClickTimeout)
                            // PA Read and AI-1 V1 settings are mutually exclusive;
                            // warn instead of silently ignoring the click.
                            if (this.props.isPcActive || this.props.isReading) {
                                window.appState?.showAiModelPopUp?.({
                                    elementKey:
                                        'Disable PA Read before enabling AI-1 V1 settings',
                                    text: true,
                                    time: 4000,
                                })
                                return
                            }
                            if (isAiSelectOpen) {
                                this.props.setAssemblyPageAi({
                                    ...this.props.appState.assembly
                                        .assemblyPageAi,
                                    isShowAiSelectContainer: false,
                                })
                                return
                            }
                            if (
                                !this.props.aiVersion &&
                                this.props.webSerial?.isConnected
                            ) {
                                return
                            }
                            const assemblyPageAi = {
                                ...this.props.appState.assembly.assemblyPageAi,
                                isShowAiSelectContainer: true,
                            }
                            this.props.setAssemblyPageAi(assemblyPageAi)
                        }}
                    >
                        <Settings size={17} strokeWidth={2.25} />
                    </button>
                    {isAi_lidarSelect && (
                        <p
                            style={{
                                position: 'absolute',
                                top,
                                marginTop: '2.5%',
                                marginLeft: '-1.5%',
                                left: left - 17,
                                zIndex: '3',
                                color: '#221e1eff',
                                fontSize: '20px',
                            }}
                        >
                            {this.resolveLiveValue(this.props.connectedTo)}
                        </p>
                    )}
                </div>
            )
        } else if (
            this.props.type === 'Magnetometer' ||
            this.props.type === 'IMU' ||
            this.props.type === 'Color_sensor'
        ) {
            const {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props

            if (isDragging) {
                return null
            }

            const height = 100 * scale
            const width = 100 * scale
            const adjustedTop = top + 15

            const baseStyle = {
                position: 'absolute',
                zIndex: 3,
                color: '#707070',
                fontSize: '20px',
                transform: 'translate(-50%, -50%)',
            }

            const renderSensor = (label, data, offsetLeft) => (
                <div
                    style={{
                        ...baseStyle,
                        top: top + 40,
                        left: left + offsetLeft,
                    }}
                >
                    {data.map(({ key, value }) =>
                        value !== undefined &&
                        value !== null &&
                        value !== '' ? (
                            <p key={key} style={{ marginBottom: 0 }}>
                                {label}_{key}: {value}
                            </p>
                        ) : null
                    )}
                </div>
            )

            const isConnect =
                this.props.connectedTo === 'E' ||
                this.props.connectedTo === 'E1' ||
                this.props.connectedTo === 'D' ||
                this.props.connectedTo === 'D1'

            return connectDragSource(
                <div>
                    <div
                        id={`${type}${this.props.index}`}
                        style={{
                            ...style,
                            left: left + 3,
                            top: adjustedTop - 12,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                    {isConnect && type === 'IMU' && (
                        <>
                            {renderSensor(
                                'acc',
                                [
                                    {
                                        key: 'x',
                                        value: this.props.accelerometer_X,
                                    },
                                    {
                                        key: 'y',
                                        value: this.props.accelerometer_Y,
                                    },
                                    {
                                        key: 'z',
                                        value: this.props.accelerometer_Z,
                                    },
                                ],
                                -30
                            )}
                            {renderSensor(
                                'gyro',
                                [
                                    { key: 'x', value: this.props.gyro_X },
                                    { key: 'y', value: this.props.gyro_Y },
                                    { key: 'z', value: this.props.gyro_Z },
                                ],
                                140
                            )}
                        </>
                    )}
                    {isConnect &&
                        type === 'Magnetometer' &&
                        renderSensor(
                            'mag',
                            [
                                { key: 'x', value: this.props.magnetometer_X },
                                { key: 'y', value: this.props.magnetometer_Y },
                                { key: 'z', value: this.props.magnetometer_Z },
                            ],
                            -30
                        )}
                    {isConnect &&
                        type === 'Color_sensor' &&
                        renderSensor(
                            'color',
                            [
                                { key: 'red', value: this.props.red },
                                { key: 'green', value: this.props.green },
                                { key: 'blue', value: this.props.blue },
                            ],
                            -44
                        )}
                </div>
            )
        } else if (this.props.type == 'joystickController') {
            var { type, left, top, scale, connectDragSource, isDragging } =
                this.props
            if (isDragging) {
                return null
            }

            var height = 80 * scale
            var width = 80 * scale

            const jcRippleBase = {
                position: 'absolute',
                borderRadius: '50%',
                background:
                    'radial-gradient(circle, rgba(0,200,255,0.8) 0%, rgba(0,150,200,0.4) 50%, rgba(0,150,200,0.1) 100%)',
                boxShadow:
                    'inset 0 0 40px rgba(0,220,255,0.6), 0 0 40px rgba(0,200,255,0.5)',
                animation:
                    'beam-expand 2.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) infinite',
                animationFillMode: 'backwards',
                width: width * 2,
                height: height * 2,
                left: left + width / 2,
                top: top + height / 2,
                transformOrigin: '50% 50%',
                transform: 'translate(-50%, -50%)',
                pointerEvents: 'none',
                zIndex: 1,
            }

            return connectDragSource(
                <div>
                    <div style={{ ...jcRippleBase, animationDelay: '0s' }} />
                    <div style={{ ...jcRippleBase, animationDelay: '0.3s' }} />
                    <div style={{ ...jcRippleBase, animationDelay: '0.7s' }} />
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left,
                            top,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    />
                </div>
            )
        } else {
            var {
                type,
                left,
                top,
                scale,
                connectDragSource,
                isDragging,
                children,
            } = this.props
            if (isDragging) {
                return null
            }

            var height = 100 * scale
            var width = 100 * scale
            let pTagTop = '10px'

            if (
                [
                    'Analog_3_Pin_Input',
                    'Analog_2_Pin_Input',
                    'PWM_3_Pin_Output',
                    'PWM_2_Pin_Output',
                    'Digital_3_Pin_Input',
                    'Digital_2_Pin_Input',
                    'Digital_3_Pin_Output',
                    'Digital_2_Pin_Output',
                    'Audio_codec',
                ].includes(this.props.type)
            ) {
                left = left + 2
                top = top + 12
            } else if (
                [
                    'tern_touch_sensor',
                    'tern_humidity_sensor',
                    'tern_gas_sensor',
                    'tern_sound_sensor',
                    'tern_tact_switch',
                ].includes(this.props.type)
            ) {
                left = left - 17
                top = top + 5
            } else if (['tern_beeper'].includes(this.props.type)) {
                left = left - 17
                top = top + 10
            } else if (
                ['DHT11', 'sound_sensor', 'IR_proximity'].includes(
                    this.props.type
                )
            ) {
                left = left + 5
                top = top + 15
            } else if (this.props.type == 'L293D_motor_driver') {
                left = left + 5
                top = top + 25
            } else if (this.props.type == 'LDR_sensor') {
                left = left + 2
                top = top + 2
                pTagTop = '40px'
            }

            return connectDragSource(
                <div>
                    <div
                        id={this.props.type + this.props.index}
                        style={{
                            ...style,
                            left: left + 3,
                            top: top - 12,
                            backgroundImage: `url(${renderCompImg(type)})`,
                            height,
                            width,
                        }}
                        onMouseDown={() => {
                            clickStartTimestamp = Date.now()
                        }}
                        onMouseUp={this.checkForLongPress}
                        onDoubleClick={() => this.typeCheck(this)}
                    >
                        {[
                            'Analog_3_Pin_Input',
                            'Analog_2_Pin_Input',
                            'Digital_3_Pin_Input',
                            'Digital_2_Pin_Input',
                            'tern_touch_sensor',
                            'tern_humidity_sensor',
                            'tern_gas_sensor',
                            'tern_sound_sensor',
                            'tern_tact_switch',
                        ].includes(this.props.type) && (
                            <p
                                style={{
                                    top: pTagTop,
                                    position: 'absolute',
                                    zIndex: '3',
                                    color: '#707070',
                                    fontSize: '20px',
                                    transform: 'translate(-50%, -50%)',
                                }}
                            >
                                {this.resolveLiveValue(this.props.connectedTo)}
                            </p>
                        )}
                    </div>
                </div>
            )
        }
    }
}

export default DragSource(
    ItemTypes.COMPONENT,
    componentSource,
    collect
)(Component1)
